import { PasswordRecovery } from "../components/PasswordRecovery";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Field, TextInput, ActionButton } from "../components/common";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3b315915-dd55-41f5-8e81-417e8bee0ca4/images/a2ccba8b1c00e483c2adefcc25885785c150564be1c1d7855d944eca970b6aca.jpeg";

export default function Login() {
  const { login, register, loginRequestor, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "", tenant_id: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data =
        mode === "requestor"
          ? await loginRequestor(form.email, form.password, form.tenant_id)
          : mode === "register"
            ? await register(form)
            : await login(form.email, form.password);
      if (mode === "salesrep" && data.user.role !== "salesperson") {
        await logout();
        toast.error("This account is not a Sales Rep. Use Sign In instead.");
        return;
      }
      if (mode === "driver" && data.user.role !== "driver") {
        await logout();
        toast.error("This account is not a Driver. Use Sign In instead.");
        return;
      }
      toast.success(`Welcome, ${data.user.name}`);
      const dest =
        data.user.role === "platform_admin"
          ? "/admin/tenants"
          : data.user.role === "requestor"
            ? "/portal"
            : data.user.role === "mechanic"
              ? "/mobilemechanic"
              : data.user.role === "driver"
              ? "/mobiledriver"
              : data.user.role === "salesperson"
                ? "/mobilerental"
                : "/dashboard";
      const returnTo = location.state?.from;
      const safeReturn = typeof returnTo === "string" && /^\/[a-z]/i.test(returnTo);
      navigate(safeReturn ? returnTo : dest, { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <div className="hidden lg:block relative w-1/2">
        <img src={HERO_IMG} alt="Oilfield truck" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#142B3B]/95 via-[#142B3B]/55 to-[#142B3B]/20" />
        <div className="absolute bottom-0 left-0 p-10">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#D8C5A3] mb-3">Multi-tenant · Modular · Field-proven</p>
          <h2 className="font-heading text-5xl font-bold text-white leading-none max-w-md">
            One platform for every barrel, tank & truck.
          </h2>
          <p className="text-white/80 mt-4 max-w-md text-sm leading-relaxed">
            SWD dispatch, frac tank rentals, fleet maintenance and shared customer & asset ledgers — enabled per tenant, billed per module.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 tactical-grid">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center gap-3 mb-8">
            <img src="/logo.png?v=5" alt="Well Fleet Logic" className="w-20 h-20 object-contain" data-testid="brand-logo" />
            <div className="text-center leading-none">
              <p className="font-heading text-3xl font-bold tracking-wide text-slate-50">WELL FLEET <span className="text-amber-500">LOGIC</span></p>
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-500/80 mt-1.5">Modular ERP</p>
            </div>
          </div>

          <div className="flex gap-2 mb-6">
            <button
              data-testid="login-tab-button"
              onClick={() => setMode("login")}
              className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors duration-200 ${
                mode === "login"
                  ? "bg-amber-500/10 text-amber-400 border-amber-500/40"
                  : "text-slate-400 border-slate-700 hover:border-slate-500"
              }`}
            >
              Sign In
            </button>
            <button
              data-testid="requestor-tab-button"
              onClick={() => setMode("requestor")}
              className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors duration-200 ${
                mode === "requestor"
                  ? "bg-amber-500/10 text-amber-400 border-amber-500/40"
                  : "text-slate-400 border-slate-700 hover:border-slate-500"
              }`}
            >
              Requester
            </button>
            <button
              data-testid="sales-rep-tab-button"
              onClick={() => setMode("salesrep")}
              className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors duration-200 ${
                mode === "salesrep"
                  ? "bg-amber-500/10 text-amber-400 border-amber-500/40"
                  : "text-slate-400 border-slate-700 hover:border-slate-500"
              }`}
            >
              Sales Rep
            </button>
            <button
              data-testid="driver-tab-button"
              onClick={() => setMode("driver")}
              className={`flex-1 py-2 rounded-md text-sm font-medium border transition-colors duration-200 ${
                mode === "driver"
                  ? "bg-amber-500/10 text-amber-400 border-amber-500/40"
                  : "text-slate-400 border-slate-700 hover:border-slate-500"
              }`}
            >
              Driver
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <Field label="Company name">
                  <TextInput testId="register-company-input" value={form.company_name} onChange={set("company_name")} required placeholder="Permian Basin Services LLC" />
                </Field>
                <Field label="Your name">
                  <TextInput testId="register-name-input" value={form.name} onChange={set("name")} required placeholder="Casey Reed" />
                </Field>
              </>
            )}
            <Field label="Email">
              <TextInput testId="login-email-input" type="email" value={form.email} onChange={set("email")} required placeholder="you@company.com" />
            </Field>
            {mode === "requestor" && <Field label="Company ID (if supplied by your administrator)"><TextInput testId="portal-company-code-input" value={form.tenant_id} onChange={set("tenant_id")} autoComplete="off" placeholder="Optional for a unique email" /></Field>}
            <Field label="Password">
              <TextInput testId="login-password-input" type="password" value={form.password} onChange={set("password")} required minLength={mode === "register" ? 8 : 4} placeholder="••••••••" />
            </Field>
            <ActionButton testId="login-submit-button" type="submit" disabled={busy} className="w-full justify-center py-2.5">
              {busy ? "Working…" : mode === "login" ? "Sign In" : mode === "requestor" ? "Sign In to Portal" : mode === "salesrep" ? "Sign In as Sales Rep" : mode === "driver" ? "Sign In as Driver" : "Create Company Account"}
            </ActionButton>
            {mode === "register" && (
              <p className="text-xs text-slate-500 leading-relaxed">
                New companies start on the <span className="text-amber-500/90">trial plan</span> with SWD, Frac Tank and Fleet modules enabled. The platform owner can adjust modules and billing anytime.
              </p>
            )}
          </form>
          {mode !== "register" && <div className="mt-4"><PasswordRecovery initialEmail={form.email} accountType={mode === "requestor" ? "requestor" : "staff"} companyId={mode === "requestor" ? form.tenant_id : ""} /></div>}
        </div>
      </div>
    </div>
  );
}
